// apps/web/src/pages/PaymentSettingsPage.tsx
//
// Super-admin only — toggle QPay processor between sandbox and production.
// The setting lives in public.system_settings.qpay_environment and is read
// by the qpay-charge and qpay-refund edge functions on every invocation.
import { useEffect, useState } from 'react';
import { Loader2, ShieldCheck, FlaskConical, Save, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { usePermissions } from '@/hooks/usePermissions';
import {
  getQPayEnvironment, setQPayEnvironment, type QPayEnvironment,
} from '@/services/payments';

export function PaymentSettingsPage() {
  const { hasRole } = usePermissions();
  const isSuperAdmin = hasRole('super_admin');

  const [current, setCurrent] = useState<QPayEnvironment | null>(null);
  const [pending, setPending] = useState<QPayEnvironment | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const env = await getQPayEnvironment();
        if (!cancelled) setCurrent(env);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'No se pudo cargar la configuración');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!isSuperAdmin) {
    return (
      <div className="container mx-auto p-6">
        <Card>
          <CardContent className="p-6 text-muted-foreground">
            Esta sección está disponible únicamente para super administradores.
          </CardContent>
        </Card>
      </div>
    );
  }

  const handleSelect = (env: QPayEnvironment) => {
    if (env === current) return;
    setPending(env);
    setConfirmOpen(true);
  };

  const handleConfirm = async () => {
    if (!pending) return;
    setSaving(true);
    setError(null);
    try {
      await setQPayEnvironment(pending);
      setCurrent(pending);
      setConfirmOpen(false);
      setPending(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo guardar el cambio');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="container mx-auto p-6 space-y-6 max-w-3xl">
      <div>
        <h1 className="text-2xl font-semibold">Configuración de pagos</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Selecciona el entorno del procesador de pagos QPay Pro. Todos los cobros y reembolsos
          futuros usarán esta configuración en tiempo real (no requiere redeploy).
        </p>
      </div>

      {error && (
        <div className="flex items-start gap-2 p-3 rounded-md bg-rose-500/10 text-rose-700 border border-rose-500/30 text-sm">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-12 text-muted-foreground">
          <Loader2 className="w-6 h-6 animate-spin" />
        </div>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>Entorno QPay Pro</span>
              {current === 'production' ? (
                <Badge className="bg-emerald-500/15 text-emerald-600 border border-emerald-500/30">
                  <ShieldCheck className="w-3 h-3 mr-1 inline" /> Producción activa
                </Badge>
              ) : (
                <Badge className="bg-amber-500/15 text-amber-600 border border-amber-500/30">
                  <FlaskConical className="w-3 h-3 mr-1 inline" /> Sandbox activo
                </Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => handleSelect('sandbox')}
                className={`text-left p-4 rounded-lg border-2 transition-all ${
                  current === 'sandbox'
                    ? 'border-amber-500 bg-amber-500/5'
                    : 'border-muted hover:border-amber-500/50'
                }`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <FlaskConical className="w-5 h-5 text-amber-600" />
                  <span className="font-medium">Sandbox</span>
                  {current === 'sandbox' && <Badge variant="outline" className="ml-auto">Actual</Badge>}
                </div>
                <p className="text-xs text-muted-foreground">
                  Ambiente de pruebas. Usa la tarjeta <code>4111 1111 1111 1111</code> con CVV 123.
                  No se realizan cargos reales.
                </p>
              </button>

              <button
                type="button"
                onClick={() => handleSelect('production')}
                className={`text-left p-4 rounded-lg border-2 transition-all ${
                  current === 'production'
                    ? 'border-emerald-500 bg-emerald-500/5'
                    : 'border-muted hover:border-emerald-500/50'
                }`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <ShieldCheck className="w-5 h-5 text-emerald-600" />
                  <span className="font-medium">Producción</span>
                  {current === 'production' && <Badge variant="outline" className="ml-auto">Actual</Badge>}
                </div>
                <p className="text-xs text-muted-foreground">
                  Ambiente real. Cualquier cobro genera una transacción contra la tarjeta del cliente
                  y se liquida en la cuenta bancaria.
                </p>
              </button>
            </div>

            <div className="text-xs text-muted-foreground border-t pt-3">
              <p className="font-medium mb-1">Importante:</p>
              <ul className="list-disc list-inside space-y-0.5">
                <li>El cambio tiene efecto inmediato — no requiere redeploy de edge functions.</li>
                <li>Las credenciales de cada entorno se gestionan como Supabase secrets.</li>
                <li>Conmutar a sandbox detiene los cobros reales hasta que se reactive producción.</li>
              </ul>
            </div>
          </CardContent>
        </Card>
      )}

      {!loading && current === 'sandbox' && (
        <Card className="border-amber-500/30">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-amber-700">
              <FlaskConical className="w-5 h-5" />
              Tarjetas de prueba (Sandbox QPay Pro)
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Usa cualquiera de estas tarjetas ficticias en la app cliente para probar el flujo
              completo de cobro sin generar cargos reales. Cualquier fecha futura sirve como
              vencimiento y el CVV puede ser cualquier número de 3 dígitos.
            </p>

            <div className="grid sm:grid-cols-2 gap-3">
              <div className="p-3 rounded-md bg-emerald-500/5 border border-emerald-500/30">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-medium text-emerald-700">Visa — Aprobada</span>
                  <Badge className="bg-emerald-500/15 text-emerald-700 border-emerald-500/30 text-[10px]">
                    APROBADA
                  </Badge>
                </div>
                <code className="block font-mono text-base tracking-wider">4111 1111 1111 1111</code>
                <p className="text-[11px] text-muted-foreground mt-1">CVV: 123 · Venc: cualquier fecha futura</p>
              </div>

              <div className="p-3 rounded-md bg-emerald-500/5 border border-emerald-500/30">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-medium text-emerald-700">Mastercard — Aprobada</span>
                  <Badge className="bg-emerald-500/15 text-emerald-700 border-emerald-500/30 text-[10px]">
                    APROBADA
                  </Badge>
                </div>
                <code className="block font-mono text-base tracking-wider">5555 5555 5555 4444</code>
                <p className="text-[11px] text-muted-foreground mt-1">CVV: 123 · Venc: cualquier fecha futura</p>
              </div>

              <div className="p-3 rounded-md bg-rose-500/5 border border-rose-500/30">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-medium text-rose-700">Visa — Rechazada</span>
                  <Badge className="bg-rose-500/15 text-rose-700 border-rose-500/30 text-[10px]">
                    RECHAZADA
                  </Badge>
                </div>
                <code className="block font-mono text-base tracking-wider">4000 0000 0000 0002</code>
                <p className="text-[11px] text-muted-foreground mt-1">CVV: 123 · Venc: cualquier fecha futura</p>
              </div>

              <div className="p-3 rounded-md bg-amber-500/5 border border-amber-500/30">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-medium text-amber-700">Visa — Fondos insuficientes</span>
                  <Badge className="bg-amber-500/15 text-amber-700 border-amber-500/30 text-[10px]">
                    INSUFICIENTE
                  </Badge>
                </div>
                <code className="block font-mono text-base tracking-wider">4000 0000 0000 9995</code>
                <p className="text-[11px] text-muted-foreground mt-1">CVV: 123 · Venc: cualquier fecha futura</p>
              </div>
            </div>

            <p className="text-[11px] text-muted-foreground border-t pt-2">
              Estas tarjetas son del set estándar de pruebas de procesadores compatibles con QPay Pro.
              Si alguna no funciona, consulta con soporte de QPay Pro las tarjetas vigentes del sandbox.
            </p>
          </CardContent>
        </Card>
      )}

      {/* ── Dialog ── */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              Cambiar a {pending === 'production' ? 'Producción' : 'Sandbox'}
            </DialogTitle>
            <DialogDescription>
              {pending === 'production' ? (
                <>
                  Estás a punto de activar el entorno <strong>de producción</strong>. Todos los cobros
                  posteriores serán transacciones reales contra las tarjetas de los clientes.
                </>
              ) : (
                <>
                  Estás a punto de activar el entorno <strong>sandbox</strong>. Todos los cobros
                  serán simulados y no generarán cargos reales.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)} disabled={saving}>
              Cancelar
            </Button>
            <Button onClick={handleConfirm} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Confirmar cambio
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
